import morphdom from 'morphdom';
import EventHandler from '@pixelunion/events';
import AsyncView from '@pixelunion/shopify-asyncview';
import ComplementaryProducts from '@pixelunion/pxs-complementary-products';
import rimg from '@pixelunion/rimg-shopify';
import ShopifySurfacePickUp from '@pixelunion/shopify-surface-pick-up';
import RecipientForm from '@pixelunion/pxs-gift-card-recipient-form';
import VariantHelper from '../helpers/VariantHelper';
import ProductGallery from './ProductGallery';
import RTE from './RTE';
import Select from './Select';
import themeUtils from '../themeUtils';
import Modal from './Modal';

export default class Product {
  constructor(el, options, sectionId) {
    this.events = new EventHandler();
    this.positionProductInfo = this.positionProductInfo.bind(this);
    this.el = el;

    this.surfacePickUp = new ShopifySurfacePickUp(this.el.querySelector('[data-surface-pick-up]'));
    this.surfacePickUp.onModalRequest(contents => {
      this.modal = new Modal(
        {
          el: this.el.querySelector('[data-modal-id="surface-pick-up"]'),
          callbacks: {
            onOpen: ({ modal }) => {
              const modalHeader = modal.querySelector('[data-modal-header]');
              const modalContents = modal.querySelector('[data-modal-contents]');

              if (this.product && this.variantHelpers) {
                const variant = this.variantHelpers.getVariantFromOptions(this.variantHelpers.getCurrentOptions());

                modalHeader.innerHTML = `<h2 class="surface-pick-up-modal__header">${this.product.title}</h2><span class="surface-pick-up-modal__subtitle">${variant.title}</span>`;
              }

              modalContents.innerHTML = contents;
            },
          },
        },
        {
          reposition: false,
        },
      );
      this.modal.open();
      this.modal.showModal();
    });

    this.recipientFormEl = this.el.querySelector('[data-recipient-form]');

    if (this.recipientFormEl) {
      this.recipientForm = new RecipientForm(this.el);
    }

    // Set up conditional options
    this.product = options.product;
    this.productSettings = options.product_settings;
    this.labels = JSON.parse(document.querySelector('[data-product-success-labels]').innerHTML);
    this.imagesLayout = options.images_layout;
    this.isQuickshop = options.isQuickshop;
    this.enableZoom = options.enable_zoom;
    this.enableVideoAutoplay = options.enable_video_autoplay;
    this.enableVideoLooping = options.enable_video_looping;
    this.enableCartRedirection = options.enable_cart_redirection;
    this.enableFixedPositioning = options.enable_fixed_positioning;
    this.productRecommendationLimit = options.product_recommendation_limit;

    this.variantHelpers = null;
    this.productMasonryLayout = null;
    this.rteViews = [];
    this.selectViews = [];

    this.productForm = this.el.querySelector('[data-product-form]');
    this.productContainer = this.el.querySelector('.product');
    this.productImages = this.el.querySelector('.product-images');
    this.productDetailsWrapper = this.el.querySelector('.product-details-wrapper');
    this.productDetails = this.el.querySelector('.product-details');
    this.productMessages = this.el.querySelector('.product-message');
    this.addToCartBtn = this.el.querySelector('.add-to-cart');
    this.productQuantityInput = this.el.querySelector('[data-product-quantity-input]');
    this.images = JSON.parse(this.el.querySelector('[data-images]').innerText);
    this.smallGallery = themeUtils.isSmall();
    this.productGallery = null;
    this.unitPrice = this.el.querySelector('[data-unit-price]');
    this.taxLine = this.el.querySelector('[data-tax-line]');
    this.complementaryProductsEl = this.el.querySelector('[data-complementary-products]');

    if (!this.product) return;

    this._initProductGallery();

    this.reloadGallery = this._reloadGallery.bind(this);
    window.addEventListener('resize', this.reloadGallery);

    this.el.querySelectorAll('.rte').forEach(rte => this.rteViews.push(new RTE({ el: rte })));
    this.el.querySelectorAll('select').forEach(select => {
      if (!select.hasAttribute('data-select-ignore')) {
        this.selectViews.push(new Select({ el: select }));
      }
    });

    this.setupVariants();

    if (this.enableFixedPositioning) {
      this.positionProductInfo();
    }

    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', event => this.addToCart(event));
    }

    if (Shopify.PaymentButton != null) {
      Shopify.PaymentButton.init();
    }

    if (this.complementaryProductsEl) {
      this.complementaryProductRecommendations = new ComplementaryProducts({
        sectionEl: this.el,
        sectionId,
        productId: this.product.id,
        productRecommendationsRoute: window.Theme.routes.product_recommendations_url,
        includeIndicatorDots: true,
        limit: this.productRecommendationLimit,
      });
    }
  }

  _initProductGallery() {
    this.productGallery = new ProductGallery(
      this.el.querySelector('[data-product-gallery]'),
      {
        product_container: this.productContainer,
        images_layout: this.imagesLayout,
        images_json: this.images,
        enable_zoom: this.enableZoom,
        enable_video_looping: this.enableVideoLooping,
        enable_video_autoplay: this.enableVideoAutoplay,
        isQuickshop: this.isQuickshop,
      },
    );
  }

  _reloadGallery() {
    if (themeUtils.isSmall() && !this.smallGallery) {
      this.productGallery.unload();
      this.productGallery = null;
      this._initProductGallery();
      this.smallGallery = true;
    }

    if (!themeUtils.isSmall() && this.smallGallery) {
      this.productGallery.unload();
      this.productGallery = null;
      this._initProductGallery();
      this.smallGallery = false;
    }
  }

  unload() {
    clearTimeout(this.positionTimeout);

    if (this.variantHelpers != null) {
      this.variantHelpers.unload();
    }
    if (this.productGallery) {
      this.productGallery.unload();
    }

    if (this.rteViews.length > 0) {
      this.rteViews = [];
    }

    if (this.selectViews.length > 0) {
      this.selectViews = [];
    }
    window.removeEventListener('resize', this.reloadGallery);
  }

  setupVariants() {
    const event = document.createEvent('Event');
    const variantDropdowns = this.el.querySelectorAll('[data-option-select]');

    this.variantHelpers = new VariantHelper({
      addToCartButton: this.el.querySelector('.add-to-cart'),
      priceFields: this.el.querySelectorAll('.product-price'),
      productForm: this.el.querySelector('[data-product-form]'),
      productGallery: this.productGallery,
      productThumbnails: this.el.querySelectorAll('.product-thumbnails img'),
      selector: variantDropdowns,
      type: 'select',
      productJSON: this.product,
      productSettings: this.productSettings,
      unitPrice: this.unitPrice,
      taxLine: this.taxLine,
      quantityInput: this.productQuantityInput,
    });
    this.variantHelpers.onVariantChange(v => {
      const id = v ? v.id : null;

      this.surfacePickUp.load(id);
    });

    if (variantDropdowns.length) {
      event.initEvent('change', true, true);
      variantDropdowns.forEach(variantDropdown => variantDropdown.dispatchEvent(event));
    } else {
      this.surfacePickUp.load(this.product.variants[0].id);
    }
  }

  positionProductInfo() {
    const detailsHeight = this.productDetails.getBoundingClientRect().height;
    let viewportHeight = window.innerHeight;

    if (document.documentElement.clientHeight < window.innerHeight) {
      viewportHeight = document.documentElement.clientHeight;
    }

    if (
      (window.innerWidth > 770)
      && (viewportHeight > detailsHeight)
    ) {
      this.triggerFixedProductInfo();
    } else {
      this.productDetails.classList.remove('product-details-fixed');
      this.productDetails.classList.remove('product-details-absolute');
    }

    return requestAnimationFrame(this.positionProductInfo);
  }

  triggerFixedProductInfo() {
    if (this.imagesLayout !== 'list') { return; }

    const { scrollTop } = window;
    const { height: imagesHeight, top: imagesOffsetTop } = this.productImages.getBoundingClientRect();
    const imagesOffsetBottom = imagesOffsetTop + imagesHeight;
    const detailsWrapperOffset = this.productDetailsWrapper.getBoundingClientRect().top;
    let { height: detailsHeight, top: detailsOffsetTop } = this.productDetails.getBoundingClientRect();
    let detailsOffsetBottom = detailsOffsetTop + detailsHeight;

    if (detailsHeight < imagesHeight) {
      // If we're far enough down the page that the content is near the window top
      if ((detailsWrapperOffset - scrollTop) < 0) {
        // Check if our details / images are out of line but low enough to "fix"
        if ((detailsOffsetBottom < imagesOffsetBottom) || (scrollTop < detailsOffsetTop)) {
          this.productDetails.classList.remove('product-details-absolute');
          this.productDetails.classList.add('product-details-fixed');
        }

        ({ height: detailsHeight, top: detailsOffsetTop } = this.productDetails.getBoundingClientRect());
        detailsOffsetBottom = detailsOffsetTop + detailsHeight;

        // If we're far enough down then this positions items near the bottom
        if ((detailsOffsetBottom >= imagesOffsetBottom) || (scrollTop >= detailsOffsetTop)) {
          this.productDetails.classList.remove('product-details-fixed');
          this.productDetails.classList.add('product-details-absolute');
        }
      } else {
        this.productDetails.classList.remove('product-details-fixed');
        this.productDetails.classList.remove('product-details-absolute');
      }
    }
  }

  addToCart(e) {
    if (this.enableCartRedirection) {
      return;
    }

    // Reload the cart page when a product is added to the cart
    if (!document.body.classList.contains('template-cart')) {
      e.preventDefault();
    }

    if (this.recipientFormEl) {
      this.recipientFormEl.classList.remove('recipient-form--has-errors');
    }

    this.productMessages.innerHTML = '';

    const formData = new FormData(this.productForm);

    fetch(
      `${window.Theme.routes.cart_add_url}.js`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      },
    )
      .then(response => {
        if (!response.ok) {
          const error = new Error();
          error.response = response;
          throw error;
        }
        return response.json();
      })
      .then(item => {
        if (!item) return;
        const message = window.Theme.addToCartSuccess
          .replace('**product**', item.title)
          .replace('**cart_link**', `<a href="${window.Theme.routes.cart_url}">${this.labels.cartLink}</a>`)
          .replace('**continue_link**', `<a href="${window.Theme.routes.all_products_collection_url}">${this.labels.continueLink}</a>`)
          .replace('**checkout_link**', `<button type="submit" name="checkout" form="checkout_form">${this.labels.checkoutLink}</button>`);

        setTimeout(
          () => {
            this.productMessages.innerHTML = message;
            this.productMessages.classList.add('success-message');
            this.productMessages.classList.remove('error-message');
            this.updateCart(item);
          },
          500,
        );
      })
      .catch(error => this.handleErrors(error));
  }

  updateCart() {
    document.querySelector('.mini-cart').classList.remove('empty');
    const miniCartItemWrapper = document.querySelector('.mini-cart-item-wrapper');
    const cartCount = document.querySelectorAll('.cart-count-number');

    AsyncView.load(
      window.Theme.routes.cart_url,
      { view: 'mini-ajax' },
    )
      .then(({ data, html }) => {
        const updatedMiniCart = document.createElement('div');
        updatedMiniCart.innerHTML = html.miniCart;
        cartCount.forEach(el => el.innerText = data.itemCount);

        morphdom(
          miniCartItemWrapper,
          updatedMiniCart.querySelector('.mini-cart-item-wrapper'),
          {
            onBeforeElUpdated: (fromEl, toEl) => {
              if (fromEl.tagName === 'IMG' && fromEl.src === toEl.src) {
                return false;
              }
              return true;
            },
          },
        );
      });

    rimg.watch(miniCartItemWrapper);
  }

  handleErrors(error) {
    error.response.json().then(err => setTimeout(
      () => {
        if (err.errors && err.errors.email) {
          this.recipientFormEl.classList.add('recipient-form--has-errors');
        } else {
          this.productMessages.innerHTML = err.description;
          this.productMessages.classList.add('error-message');
          this.productMessages.classList.remove('success-message');
        }
      },
      1000,
    ));
  }
}
